import {
    Injectable,
    PLATFORM_ID,
    Inject,
    InjectionToken,
    Renderer2,
    RendererFactory2,
} from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { delay, filter, throttleTime, take } from 'rxjs/operators';
import { XHttpService } from '@app/core/services/x-http.service';
import { AdsDirective } from '../directives/ads.directive';
import { ADS_PARAMS } from '@app/ads/tokens';
import { Helper } from '@app/core/helper';
import { StateService } from '@app/core/services/state.service';
import { environment } from '@env/environment';

function unsubscribeSubscription(subscription: any) {
    subscription && subscription.unsubscribe();
}

@Injectable({
    providedIn: 'root',
})
export class AdsService {
    private rend!: Renderer2;
    public readonly loading$: BehaviorSubject<boolean> =
        new BehaviorSubject<boolean>(true);
    private its: AdsDirective[] = [];
    private posMap: any = {};
    private routeChanged: boolean = false;
    private psb: any;
    private n$: Subject<boolean> = new Subject();
    private posOrd: string[] = ['in_body'];
    environment: any = environment;
    constructor(
        @Inject(PLATFORM_ID)
        private pid: any,
        @Inject(DOCUMENT)
        private doc: Document,
        private api: XHttpService,
        private r: Router,
        private ar: ActivatedRoute,
        private ss: StateService,
        private rf: RendererFactory2,
    ) {
        this.r.events.subscribe((val) => {
            if (val instanceof NavigationEnd) {
                this.routeChanged = true;
                this.n$.next(!0);
            }
        });
        this.n$
            .pipe(throttleTime(100, void 0, { leading: true }))
            .subscribe(() => {
                if (isPlatformBrowser(this.pid)) {
                    if (this.routeChanged) {
                        let sbs: any;
                        sbs && unsubscribeSubscription(sbs);
                        this.getProviderValue(ADS_PARAMS).then(
                            (pp$) => {
                                sbs = pp$
                                    .pipe(
                                        filter((x) => x !== null),
                                        take(1)
                                    )
                                    .subscribe((res: any) => {
                                        pp$.next(null);
                                        this.req(res);
                                        this.routeChanged = false;
                                    });
                            },
                            () => {
                                this.req({});
                                this.routeChanged = false;
                            }
                        );
                    }
                }
            });
    }

    bind(dir: AdsDirective): any {
        this.its.push(dir);
        if (!this.posMap[dir.normPos()]) {
            this.posMap[dir.normPos()] = new BehaviorSubject([[], []]);
        }
        return this.posMap[dir.normPos()].pipe(delay(dir.ord()));
    }

    unbind(dir: AdsDirective): void {
        const idx = this.its.indexOf(dir);
        if (idx >= 0) {
            this.its.splice(idx, 1);
        }
    }

    query(pageParams: any): any {
        const params = {
            uri: this.doc.location.pathname.slice(3),
            language: this.ss.language.code,
        };
        Object.assign(params, pageParams);
        return this.api.get(
            ':silent:/v3.2/banner/index',
            this.api.httpParams(params)
        );
    }

    private req(pageParams: any): void {
        const bannersMap: any = {};
        let rtlFilter = (pos: string) => {
            let delId: any = [];
            for (let i = 0; i < bannersMap[pos].length; i++) {
                if (bannersMap[pos][i].t === 1) {
                    let items: any[] = bannersMap[pos][i].p.items.filter(
                        (it: any) => {
                            return this.ss.dir === 'ltr' ? !it.rtl : !!it.rtl;
                        }
                    );
                    if (items.length > 0) {
                        bannersMap[pos][i].p.items = items;
                    } else if (items.length === 0) {
                        delId.push(bannersMap[pos][i].id);
                    }
                }
            }
            if (
                delId.length > 0 &&
                (this.ss.dir === 'ltr' ||
                    (this.ss.dir !== 'ltr' &&
                        delId.length < bannersMap[pos].length))
            ) {
                bannersMap[pos] = bannersMap[pos].filter((b: any) => {
                    return !delId.find((el: any) => {
                        return el === b.id;
                    });
                });
            }
        };
        this.loading$.next(true);
        let q = this.query(pageParams).subscribe(
            (res: any) => {
                let banners: any = res;
                this.loading$.next(false);
                for (let banner of banners) {
                    if (Array.isArray(banner.locs)) {
                        for (let loc of banner.locs) {
                            if (!bannersMap[loc]) {
                                bannersMap[loc] = [];
                            }
                            bannersMap[loc].push(banner);
                        }
                    }
                }
                if (bannersMap['left_bar']) rtlFilter('left_bar');
                if (bannersMap['right_bar']) rtlFilter('right_bar');
                for (let position of this.posOrd) {
                    if (!bannersMap[position]) {
                        continue;
                    }
                    const sorted = Helper.shuffle(bannersMap[position]);
                    if (this.posMap[position]) {
                        this.posMap[position].next([sorted, banners]);
                    } else {
                        this.posMap[position] = new BehaviorSubject([
                            sorted,
                            banners,
                        ]);
                    }
                }
                for (let position in this.posMap) {
                    if (!bannersMap[position]) {
                        this.posMap[position].next([[], banners]);
                    }
                }
                q.unsubscribe();
            },
            () => {
                this.loading$.next(false);
            }
        );
    }

    click(banner: any, position: string): void {
        this.api
            .post(':silent:/v3.2/clk', { id: banner.id, pos: position })
            .subscribe(() => {});

    }

    private tout: any;
    private views: any = [];

    view(banner: any, position: string): void {
        this.views.push({ id: banner.id, pos: position });
        this.tout && clearTimeout(this.tout);
        this.tout = setTimeout(() => {
            this.api
                .post(':silent:/v3.2/vue', { p: this.views })
                .subscribe(() => {});
            this.tout = null;
            this.views = [];
        }, 100);
    }

    getProviderValue(token: InjectionToken<any>): Promise<any> {
        return new Promise((resolve, reject) => {
            try {
                const component = this.arc;
                if (component) {
                    component.ɵcmp.providersResolver(
                        null,
                        (providers: any[]) => {
                            for (let p of providers) {
                                if (p.provide === token && p.useValue) {
                                    resolve(p.useValue);
                                    return;
                                }
                            }
                            reject('not found');
                        }
                    );
                }
            } catch (e) {
                reject(e);
            }
        });
    }

    get arc(): any {
        let route = this.ar;
        let component = this.ar.component;
        while (route.firstChild) {
            route = route.firstChild;
            if (route.component) {
                component = route.component;
            }
        }
        return component;
    }


}
